import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CommonDialog from '../../common/CommonDialog';
import useGlobalStore from '../../store/useGlobalStore';
import Billing from './Billing/Billing';
import CustomerNotifications from './CustomerNotifications/CustomerNotifications';
import InventorySettings from './Inventory/InventorySettings';
import Notification from './Notification/Notification';
import OnlineBooking from './OnlineBooking/OnlineBooking';
import PasswordAndSecurity from './PasswordAndSecurity/PasswordAndSecurity';
import PersonalInfo from './PersonalInfo/PersonalInfo';
import Pricing from './Pricing/Pricing';
import SavedServices from './SavedServices/SavedServices';
import ServiceOffered from './ServiceOffered/ServiceOffered';
import ShopDetails from './ShopDetails/ShopDetails';
import ShopHours from './ShopHours/ShopHours';
import './style.css';

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function Shop() {
    const [value, setValue] = React.useState(0);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isSettingsDirty, resetSettingsDirty } = useGlobalStore();
    const [discardOpen, setDiscardOpen] = useState(false);
    const [pendingTabNav, setPendingTabNav] = useState(null);

    useEffect(() => {
        const path = pathname.split('/');
        if (path.includes('personal-information')) setValue(0);
        else if (path.includes('shop-details')) setValue(1);
        else if (path.includes('appointments-reviews')) setValue(2);
        else if (path.includes('customer-notifications')) setValue(3);
        else if (path.includes('services-offered')) setValue(4);
        else if (path.includes('invoice-and-fees')) setValue(5);
        else if (path.includes('saved-services')) setValue(6);
        else if (path.includes('inventory')) setValue(7);
        else if (path.includes('shop-hours')) setValue(8);
        else if (path.includes('notifications')) setValue(9);
        else if (path.includes('billing')) setValue(10);
    }, [pathname]);

    const doTabNavigate = (newValue) => {
        setValue(newValue);
        switch (newValue) {
            case 0:
                navigate('/account-settings/personal-information');
                break;
            case 1:
                navigate('/account-settings/shop-details');
                break;
            case 2:
                navigate('/account-settings/appointments-reviews');
                break;
            case 3:
                navigate('/account-settings/customer-notifications');
                break;
            case 4:
                navigate('/account-settings/services-offered');
                break;
            case 5:
                navigate('/account-settings/invoice-and-fees');
                break;
            case 6:
                navigate('/account-settings/saved-services');
                break;
            case 7:
                navigate('/account-settings/inventory');
                break;
            case 8:
                navigate('/account-settings/shop-hours');
                break;
            case 9:
                navigate('/account-settings/notifications');
                break;
            case 10:
                navigate('/account-settings/billing');
                break;
            default:
                navigate('/account-settings/personal-information');
        }
    };

    const handleChange = (event, newValue) => {
        if (isSettingsDirty) {
            setPendingTabNav(newValue);
            setDiscardOpen(true);
        } else {
            doTabNavigate(newValue);
        }
    };

    const handleDiscardConfirm = () => {
        resetSettingsDirty();
        setDiscardOpen(false);
        if (pendingTabNav !== null) {
            doTabNavigate(pendingTabNav);
            setPendingTabNav(null);
        }
    };

    const handleDiscardCancel = () => {
        setDiscardOpen(false);
        setPendingTabNav(null);
    };

    return (
        <Box padding="32px 24px" sx={{ bgcolor: 'background.main', width: '100%' }}>
            <Box display="flex" fontSize="24px" mb="32px" pl="10px">
                <Typography variant="h4" fontWeight={400}>
                    Account Settings
                </Typography>
            </Box>
            <Grid container sm={12} style={{ overflowX: 'hidden', maxWidth: '100%' }}>
                <Tabs
                    value={value}
                    onChange={handleChange}
                    textColor="primary"
                    variant="scrollable"
                    allowScrollButtonsMobile
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs example"
                    ScrollButtonComponent={(props) => {
                        const { direction, disabled, ...rest } = props;

                        if (disabled) return null;

                        return (
                            <IconButton
                                {...rest}
                                sx={{
                                    backgroundColor: '#006CDD',
                                    color: '#fff',
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    margin: '4px',
                                    '&:hover': {
                                        backgroundColor: '#005bb5',
                                    },
                                }}
                            >
                                {direction === 'left' ? <ArrowBack style={{ fontSize: '20px' }} /> : <ArrowForward style={{ fontSize: '20px' }} />}
                            </IconButton>
                        );
                    }}
                    sx={{
                        '&.MuiTabs-root': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                >
                    <Tab label="Personal Information" {...a11yProps(0)} />
                    <Tab label="Shop Details" {...a11yProps(1)} />
                    <Tab label="Appointments & Reviews" {...a11yProps(2)} />
                    <Tab label="Customer Notifications" {...a11yProps(3)} />
                    <Tab label="Services Offered" {...a11yProps(4)} />
                    <Tab label="Invoices & Fees" {...a11yProps(5)} />
                    <Tab label="Saved Services" {...a11yProps(6)} />
                    <Tab label="Inventory & Pricing" {...a11yProps(7)} />
                    <Tab label="Shop Hours" {...a11yProps(8)} />
                    <Tab label="Shop Notifications" {...a11yProps(9)} />
                    <Tab label="Billing" {...a11yProps(10)} />
                </Tabs>
            </Grid>

            <CustomTabPanel value={value} index={0}>
                <PersonalInfo />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
                <ShopDetails />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={2}>
                <OnlineBooking />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <CustomerNotifications />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={4}>
                <ServiceOffered />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={5}>
                <Pricing />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={6}>
                <SavedServices />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={7}>
                <InventorySettings />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={8}>
                <ShopHours />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={9}>
                <Notification />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={10}>
                <Billing />
            </CustomTabPanel>

            {/* Discard dialog for unsaved notification template changes */}
            <CommonDialog
                loader={false}
                open={discardOpen}
                onClose={handleDiscardCancel}
                onSave={handleDiscardConfirm}
                dialogTitle="⚠️ Discard message changes?"
                dialogSubtitle="You have unsaved changes to your messages. Discard changes or continue editing? "
                saveButtonText="Discard"
                saveButtonType="error"
                cancelButtonText="CANCEL"
            />
        </Box>
    );
}
